import React, { useEffect, useMemo, useState } from "react";
import ExcelJS from "exceljs";
import {
  Box,
  Button,
  Collapse,
  CircularProgress,
  IconButton,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Checkbox,
  Typography,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import TrademarkResultCard from "./TrademarkResultCard";
import {
  AU_SEARCH_RESULTS,
  AU_STATUS_OPTIONS,
  AU_TRADEMARK_TYPE_OPTIONS,
  EU_SEARCH_RESULTS,
  EU_STATUS_OPTIONS,
  US_SEARCH_RESULTS,
} from "./tmSearchData";

const TRADEMARK_SEARCH_RESULTS_STORAGE_KEY = "trademark-search-results";
const TRADEMARK_SEARCH_UI_STATE_STORAGE_KEY = "trademark-search-ui-state";
const TRADEMARK_SEARCH_PRESERVE_ONCE_STORAGE_KEY =
  "trademark-search-preserve-once";

const DEFAULT_FORM_STATE = {
  markName: "",
  jurisdictions: ["us"],
  legalStatus: "all",
  goodsDescription: "",
  applicantName: "",
  owner: "",
  entityType: "all",
  markFeature: "all",
  renewalStatus: "all",
  trademarkType: "all",
  applicationDateFrom: "",
  applicationDateTo: "",
  registrationDateFrom: "",
  registrationDateTo: "",
  proposedGoods: "",
};

const JURISDICTION_OPTIONS = [
  { value: "us", label: "United States" },
  { value: "eu", label: "European Union" },
  { value: "au", label: "Australia" },
];

const LEGAL_STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "live", label: "Live" },
  { value: "pending", label: "Pending" },
  { value: "registered", label: "Registered" },
  { value: "dead", label: "Dead" },
];

const CANADA_ENTITY_TYPE_OPTIONS = [
  { value: "all", label: "All Entity Types" },
  { value: "INDIVIDUAL", label: "Individual" },
  { value: "COMPANY", label: "Company" },
  { value: "PARTNERSHIP", label: "Partnership" },
  { value: "ASSOCIATION", label: "Association" },
];

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#edf4fb",
  },
};

const MOCK_SEARCH_DELAY_MS = 1200;
const MAX_RESULTS_CAP = 500;
const RESULTS_PER_PAGE = 20;
const JURISDICTION_SORT_PRIORITY = {
  "United States": 0,
  "European Union": 1,
  Australia: 2,
};

const formatDateForExport = (dateValue) => {
  if (dateValue === null || dateValue === undefined || dateValue === "") {
    return "-";
  }

  if (dateValue === "-") {
    return "-";
  }

  const parts = dateValue.split("-");

  if (parts.length !== 3) {
    return dateValue;
  }

  return parts[2] + "-" + parts[1] + "-" + parts[0];
};

const getSortableDateValue = (dateValue) => {
  if (dateValue === null || dateValue === undefined) {
    return null;
  }

  const normalizedValue = String(dateValue).trim();

  if (!normalizedValue || normalizedValue === "-") {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
    const parsedIsoDate = Date.parse(normalizedValue + "T00:00:00Z");
    return Number.isNaN(parsedIsoDate) ? null : parsedIsoDate;
  }

  if (/^\d{2}-\d{2}-\d{4}$/.test(normalizedValue)) {
    const [day, month, year] = normalizedValue.split("-");
    const parsedDdMmYyyyDate = Date.parse(`${year}-${month}-${day}T00:00:00Z`);
    return Number.isNaN(parsedDdMmYyyyDate) ? null : parsedDdMmYyyyDate;
  }

  const parsedDate = Date.parse(normalizedValue);
  return Number.isNaN(parsedDate) ? null : parsedDate;
};

const sanitizeInternationalClassValue = (value) => {
  return String(value || "").replace(/\D/g, "");
};

const getValidTrademarkIdPart = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const trimmedValue = String(value).trim();

  if (!trimmedValue || trimmedValue === "-") {
    return null;
  }

  return trimmedValue;
};

const buildTrademarkResultId = (result, index) => {
  const jurisdictionPart =
    getValidTrademarkIdPart(result?.jurisdiction) ||
    getValidTrademarkIdPart(result?.markBasis) ||
    "unknown-jurisdiction";
  const applicationPart =
    getValidTrademarkIdPart(result?.applicationNo) ||
    getValidTrademarkIdPart(result?.applicationNumber) ||
    getValidTrademarkIdPart(result?.registrationNo) ||
    getValidTrademarkIdPart(result?.id) ||
    "result-" + index;

  return `${jurisdictionPart}-${applicationPart}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
};

const normalizeTrademarkResult = (result, index) => ({
  ...result,
  id: buildTrademarkResultId(result, index),
  markName:
    result?.markName ||
    result?.wordMarkSpecification?.verbalElement ||
    result?.verbalElement ||
    "-",
  jurisdiction:
    result?.jurisdiction ||
    (result?.markBasis === "EU_TRADEMARK" ? "European Union" : "-"),
  legalStatus: result?.legalStatus || result?.status || "-",
  applicationNo: result?.applicationNo || result?.applicationNumber || "-",
  registrationNo: result?.registrationNo || "-",
  classes: Array.isArray(result?.classes)
    ? result.classes
    : Array.isArray(result?.niceClasses)
      ? result.niceClasses
      : Array.isArray(result?.internationalClasses)
        ? result.internationalClasses
      : [],
  goodsDescription: result?.goodsDescription || "-",
  applicationDate: result?.applicationDate || "-",
  registrationDate: result?.registrationDate || "-",
  renewalDate: result?.renewalDate || result?.expiryDate || "-",
  statusDate: result?.statusDate || "-",
  matchScore: result?.matchScore || "-",
  iconType: result?.iconType || "description",
  trademarkImage: result?.trademarkImage || null,
  applicantName:
    result?.applicantName ||
    result?.applicants?.map((applicant) => applicant?.name || applicant?.identifier).filter(Boolean).join(", ") ||
    "-",
  markFeature: result?.markFeature || "-",
  renewalStatus: result?.renewalStatus || "-",
  trademarkType: result?.trademarkType || "-",
  owner: result?.owner || "-",
  applicationDateSortValue: getSortableDateValue(result?.applicationDate),
  registrationDateSortValue: getSortableDateValue(result?.registrationDate),
});

const getJurisdictionSortPriority = (jurisdiction) => {
  return JURISDICTION_SORT_PRIORITY[jurisdiction] ?? Number.MAX_SAFE_INTEGER;
};

const filterMockTrademarkResults = (results, { markName, internationalClasses }) => {
  const normalizedMarkName = String(markName || "").trim().toLowerCase();
  const selectedClasses = Array.from(
    new Set(internationalClasses.map(sanitizeInternationalClassValue).filter(Boolean))
  );

  return results.filter((result) => {
    const resultMarkName = String(result?.markName || "").toLowerCase();
    const resultClasses = new Set(
      Array.isArray(result?.classes)
        ? result.classes.map((classValue) => sanitizeInternationalClassValue(classValue))
        : []
    );

    const matchesMarkName =
      !normalizedMarkName || resultMarkName.includes(normalizedMarkName);
    const matchesClass =
      selectedClasses.length === 0 ||
      selectedClasses.every((selectedClass) => resultClasses.has(selectedClass));

    return matchesMarkName && matchesClass;
  });
};

const getStoredSearchResults = () => {
  try {
    return JSON.parse(
      sessionStorage.getItem(TRADEMARK_SEARCH_RESULTS_STORAGE_KEY) || "[]"
    );
  } catch (error) {
    return [];
  }
};

const getStoredSearchUiState = () => {
  try {
    return JSON.parse(
      sessionStorage.getItem(TRADEMARK_SEARCH_UI_STATE_STORAGE_KEY) || "{}"
    );
  } catch (error) {
    return {};
  }
};

const TrademarkSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const shouldRestoreFromDetail =
    Boolean(location.state?.restoreSearchState) ||
    sessionStorage.getItem(TRADEMARK_SEARCH_PRESERVE_ONCE_STORAGE_KEY) === "true";
  const storedUiState = shouldRestoreFromDetail ? getStoredSearchUiState() : {};
  const storedSearchResults = shouldRestoreFromDetail
    ? getStoredSearchResults()
    : [];
  
  const [formValues, setFormValues] = useState(
    storedUiState.formValues || DEFAULT_FORM_STATE
  );
  const [internationalClasses, setInternationalClasses] = useState(
    Array.isArray(storedUiState.internationalClasses) &&
      storedUiState.internationalClasses.length > 0
      ? storedUiState.internationalClasses
      : [""]
  );
  const [showResults, setShowResults] = useState(
    Boolean(
      shouldRestoreFromDetail &&
        storedUiState.showResults &&
        storedSearchResults.length > 0
    )
  );
  const [selectedResultIds, setSelectedResultIds] = useState(
    Array.isArray(storedUiState.selectedResultIds)
      ? storedUiState.selectedResultIds
      : []
  );
  const [exportType, setExportType] = useState(storedUiState.exportType || "excel");
  const [searchResults, setSearchResults] = useState(storedSearchResults);
  const [isSearching, setIsSearching] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(
    Boolean(storedUiState.showAdvancedFilters)
  );
  const [sortBy, setSortBy] = useState(storedUiState.sortBy || "applicationDate");
  const [sortOrder, setSortOrder] = useState(storedUiState.sortOrder || "desc");
  const [currentPage, setCurrentPage] = useState(storedUiState.currentPage || 1);
  const [totalResultsCount, setTotalResultsCount] = useState(
    storedUiState.totalResultsCount || 0
  );

  const isEuJurisdiction = formValues.jurisdictions.includes("eu");
  const isAuJurisdiction = formValues.jurisdictions.includes("au");
  const isUsJurisdiction = formValues.jurisdictions.includes("us");
  const activeStatusOptions = isEuJurisdiction && !isUsJurisdiction && !isAuJurisdiction
    ? EU_STATUS_OPTIONS
    : isAuJurisdiction && !isUsJurisdiction && !isEuJurisdiction
      ? AU_STATUS_OPTIONS
      : LEGAL_STATUS_OPTIONS;

  useEffect(() => {
    if (shouldRestoreFromDetail) {
      sessionStorage.removeItem(TRADEMARK_SEARCH_PRESERVE_ONCE_STORAGE_KEY);
      return;
    }

    sessionStorage.removeItem(TRADEMARK_SEARCH_RESULTS_STORAGE_KEY);
    sessionStorage.removeItem(TRADEMARK_SEARCH_UI_STATE_STORAGE_KEY);
  }, [shouldRestoreFromDetail]);

  useEffect(() => {
    sessionStorage.setItem(
      TRADEMARK_SEARCH_UI_STATE_STORAGE_KEY,
      JSON.stringify({
        formValues,
        internationalClasses,
        showResults,
        selectedResultIds,
        exportType,
        showAdvancedFilters,
        sortBy,
        sortOrder,
        currentPage,
        totalResultsCount,
      })
    );
  }, [
    exportType,
    formValues,
    internationalClasses,
    selectedResultIds,
    showAdvancedFilters,
    showResults,
    sortBy,
    sortOrder,
    currentPage,
    totalResultsCount,
  ]);

  // Clear sessionStorage on unmount unless this navigation explicitly preserves it.
  useEffect(() => {
    return () => {
      if (
        sessionStorage.getItem(TRADEMARK_SEARCH_PRESERVE_ONCE_STORAGE_KEY) !== "true"
      ) {
        sessionStorage.removeItem(TRADEMARK_SEARCH_RESULTS_STORAGE_KEY);
        sessionStorage.removeItem(TRADEMARK_SEARCH_UI_STATE_STORAGE_KEY);
      }
    };
  }, []);

  const fetchTrademarkResults = async () => {
    setIsSearching(true);

    try {
      let allResults = [];
      const collectionPromises = [];

      // Collect EU results
      if (formValues.jurisdictions.includes("eu")) {
        collectionPromises.push(
          new Promise((resolve) => {
            setTimeout(() => {
              const euResults = filterMockTrademarkResults(
                (EU_SEARCH_RESULTS || []).map((result, index) =>
                  normalizeTrademarkResult(result, index)
                ),
                {
                  markName: formValues.markName,
                  internationalClasses,
                }
              );
              resolve(euResults);
            }, MOCK_SEARCH_DELAY_MS / 2);
          })
        );
      }

      // Collect AU results
      if (formValues.jurisdictions.includes("au")) {
        collectionPromises.push(
          new Promise((resolve) => {
            setTimeout(() => {
              const auResults = filterMockTrademarkResults(
                (AU_SEARCH_RESULTS || []).map((result, index) =>
                  normalizeTrademarkResult(result, index)
                ),
                {
                  markName: formValues.markName,
                  internationalClasses,
                }
              );
              resolve(auResults);
            }, MOCK_SEARCH_DELAY_MS / 2);
          })
        );
      }

      // Collect US results
      if (formValues.jurisdictions.includes("us")) {
        collectionPromises.push(
          new Promise((resolve) => {
            setTimeout(() => {
              const usResults = filterMockTrademarkResults(
                (US_SEARCH_RESULTS || []).map((result, index) =>
                  normalizeTrademarkResult(result, index)
                ),
                {
                  markName: formValues.markName,
                  internationalClasses,
                }
              );
              resolve(usResults);
            }, MOCK_SEARCH_DELAY_MS / 2);
          })
        );
      }

      // Wait for all collection promises to complete
      const collectedResults = await Promise.all(collectionPromises);
      allResults = collectedResults.flat();

      // Store total count before capping
      const totalCount = allResults.length;
      setTotalResultsCount(totalCount);

      // Cap results to MAX_RESULTS_CAP
      const cappedResults = allResults.slice(0, MAX_RESULTS_CAP);
      const nextSearchResults = cappedResults.map((result, index) =>
        normalizeTrademarkResult(result, index)
      );

      setSearchResults(nextSearchResults);
      sessionStorage.setItem(
        TRADEMARK_SEARCH_RESULTS_STORAGE_KEY,
        JSON.stringify(nextSearchResults)
      );
      setShowResults(true);
      setSelectedResultIds([]);
      setCurrentPage(1);
      setSortBy("applicationDate");
      setSortOrder("desc");
    } catch (error) {
      toast.error("Failed to fetch trademark results.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();

    if (!formValues.markName.trim()) {
      toast.error("Mark Name is required to search.");
      return;
    }

    fetchTrademarkResults();
  };

  const getSortedAndPaginatedResults = useMemo(() => {
    // Sort results
    const sorted = [...searchResults].sort((a, b) => {
      const jurisdictionPriorityDifference =
        getJurisdictionSortPriority(a.jurisdiction) -
        getJurisdictionSortPriority(b.jurisdiction);

      if (jurisdictionPriorityDifference !== 0) {
        return jurisdictionPriorityDifference;
      }

      const aValue =
        sortBy === "registrationDate"
          ? a.registrationDateSortValue
          : a.applicationDateSortValue;
      const bValue =
        sortBy === "registrationDate"
          ? b.registrationDateSortValue
          : b.applicationDateSortValue;

      if (aValue === null && bValue === null) {
        return String(a.markName || "").localeCompare(String(b.markName || ""));
      }

      if (aValue === null) {
        return 1;
      }

      if (bValue === null) {
        return -1;
      }

      if (aValue === bValue) {
        return String(a.markName || "").localeCompare(String(b.markName || ""));
      }

      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    });

    // Paginate results
    const startIndex = (currentPage - 1) * RESULTS_PER_PAGE;
    const endIndex = startIndex + RESULTS_PER_PAGE;
    return sorted.slice(startIndex, endIndex);
  }, [searchResults, sortBy, sortOrder, currentPage]);

  const totalPages = Math.ceil(searchResults.length / RESULTS_PER_PAGE);

  const getDisplayedResultsInfo = () => {
    const startNum = (currentPage - 1) * RESULTS_PER_PAGE + 1;
    const endNum = Math.min(currentPage * RESULTS_PER_PAGE, searchResults.length);
    if (totalResultsCount > MAX_RESULTS_CAP) {
      return `Showing ${startNum}-${endNum} of ${MAX_RESULTS_CAP} (${totalResultsCount} total results available)`;
    }
    return `Showing ${startNum}-${endNum} of ${searchResults.length} results`;
  };

  const allSelected = useMemo(() => {
    return getSortedAndPaginatedResults.length > 0 && getSortedAndPaginatedResults.every((result) => selectedResultIds.includes(result.id));
  }, [getSortedAndPaginatedResults, selectedResultIds]);

  const handleJurisdictionChange = (event) => {
    let newJurisdictions = event.target.value;

    // MUI sometimes gives string in autofill cases
    if (typeof newJurisdictions === "string") {
      newJurisdictions = newJurisdictions.split(",");
    }

    // Ensure array
    if (!Array.isArray(newJurisdictions)) {
      newJurisdictions = [];
    }

    // If all unchecked, keep default US
    if (newJurisdictions.length === 0) {
      newJurisdictions = ["us"];
    }

    setInternationalClasses([""]);
    setShowAdvancedFilters(false);
    setShowResults(false);
    setSearchResults([]);

    setFormValues((prev) => ({
      ...prev,
      jurisdictions: newJurisdictions,
      legalStatus: "all",
      goodsDescription: "",
      applicantName: "",
      owner: "",
      entityType: "all",
      markFeature: "all",
      renewalStatus: "all",
      trademarkType: "all",
      applicationDateFrom: "",
      applicationDateTo: "",
      registrationDateFrom: "",
      registrationDateTo: "",
      proposedGoods: "",
    }));
  };

  const handleFieldChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSortByChange = (event) => {
    setCurrentPage(1);
    setSortBy(event.target.value);
  };

  const handleSortOrderToggle = () => {
    setCurrentPage(1);
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const handleReset = () => {
    setFormValues(DEFAULT_FORM_STATE);
    setInternationalClasses([""]);
    setShowResults(false);
    setSearchResults([]);
    setSelectedResultIds([]);
    setExportType("excel");
    setSortBy("applicationDate");
    setSortOrder("desc");
    setCurrentPage(1);
    setTotalResultsCount(0);
    sessionStorage.removeItem(TRADEMARK_SEARCH_RESULTS_STORAGE_KEY);
    sessionStorage.removeItem(TRADEMARK_SEARCH_UI_STATE_STORAGE_KEY);
  };

  const handleToggleResult = (resultId) => {
    setSelectedResultIds((prev) =>
      prev.includes(resultId)
        ? prev.filter((id) => id !== resultId)
        : prev.concat(resultId)
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedResultIds(searchResults.map((result) => result.id));
      return;
    }

    setSelectedResultIds([]);
  };

  const getResultsForExport = () => {
    if (selectedResultIds.length > 0) {
      return searchResults.filter((result) => selectedResultIds.includes(result.id));
    }

    return searchResults;
  };

  const downloadBlob = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    const exportResults = getResultsForExport();

    if (exportResults.length === 0) {
      toast.error("No trademark results available to export.");
      return;
    }

    const rows = exportResults.map((result) => ({
      applicationNumber: result.applicationNo,
      markName: result.markName,
      logo: result.markName
        .split(" ")
        .map((word) => word[0])
        .join("")
        .slice(0, 2),
      country: result.jurisdiction,
      legalStatus: result.legalStatus,
      internationalClasses: result.classes.join(", "),
      goodsAndServices: result.goodsDescription,
      applicantName: result.applicantName || "-",
      markFeature: result.markFeature || "-",
      renewalStatus: result.renewalStatus || "-",
      applicationDate: formatDateForExport(result.applicationDate),
      registrationDate: formatDateForExport(result.registrationDate),
      renewalDate: formatDateForExport(result.renewalDate),
    }));

    try {
      if (exportType === "excel") {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Trademark Results");

        worksheet.columns = [
          { header: "Application Number", key: "applicationNumber", width: 22 },
          { header: "Mark Name", key: "markName", width: 24 },
          { header: "Logo", key: "logo", width: 12 },
          { header: "Country Name", key: "country", width: 22 },
          { header: "Legal Status", key: "legalStatus", width: 18 },
          { header: "International Classes", key: "internationalClasses", width: 22 },
          { header: "Applicant Name", key: "applicantName", width: 28 },
          { header: "Mark Feature", key: "markFeature", width: 18 },
          { header: "Renewal Status", key: "renewalStatus", width: 24 },
          { header: "Goods and Services", key: "goodsAndServices", width: 40 },
          { header: "Application Date", key: "applicationDate", width: 18 },
          { header: "Registration Date", key: "registrationDate", width: 18 },
          { header: "Renewal Date", key: "renewalDate", width: 18 },
        ];

        rows.forEach((row) => worksheet.addRow(row));

        const headerRow = worksheet.getRow(1);
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFDCEBFA" },
          };
          cell.border = {
            top: { style: "thin", color: { argb: "FFC5D3E0" } },
            left: { style: "thin", color: { argb: "FFC5D3E0" } },
            bottom: { style: "thin", color: { argb: "FFC5D3E0" } },
            right: { style: "thin", color: { argb: "FFC5D3E0" } },
          };
        });

        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) {
            return;
          }

          row.eachCell((cell) => {
            cell.alignment = { vertical: "top", wrapText: true };
            cell.border = {
              top: { style: "thin", color: { argb: "FFE2E8F0" } },
              left: { style: "thin", color: { argb: "FFE2E8F0" } },
              bottom: { style: "thin", color: { argb: "FFE2E8F0" } },
              right: { style: "thin", color: { argb: "FFE2E8F0" } },
            };
          });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        downloadBlob(
          new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
          "trademark_search_results.xlsx"
        );
        toast.success("Trademark results exported to Excel.");
        return;
      }

      if (exportType === "csv") {
        const headers = [
          "Application Number",
          "Mark Name",
          "Logo",
          "Country Name",
          "Legal Status",
          "International Classes",
          "Applicant Name",
          "Mark Feature",
          "Renewal Status",
          "Goods and Services",
          "Application Date",
          "Registration Date",
          "Renewal Date",
        ];

        const csvRows = rows.map((row) =>
          [
            row.applicationNumber,
            row.markName,
            row.logo,
            row.country,
            row.legalStatus,
            row.internationalClasses,
            row.applicantName,
            row.markFeature,
            row.renewalStatus,
            row.goodsAndServices,
            row.applicationDate,
            row.registrationDate,
            row.renewalDate,
          ]
            .map((value) => {
              return '"' + String(value).split('"').join('""') + '"';
            })
            .join(",")
        );

        downloadBlob(
          new Blob([headers.join(",") + "\n" + csvRows.join("\n")], {
            type: "text/csv;charset=utf-8;",
          }),
          "trademark_search_results.csv"
        );
        toast.success("Trademark results exported to CSV.");
        return;
      }

      toast.info("PDF export is not available yet. Please use Excel.");
    } catch (error) {
      toast.error("Failed to export trademark results.");
    }
  };

  const handleViewDetail = (result) => {
    sessionStorage.setItem(TRADEMARK_SEARCH_PRESERVE_ONCE_STORAGE_KEY, "true");
    navigate("/trademark-search/" + result.id, {
      state: {
        result,
        searchResults,
      },
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        backgroundColor: "#f4f7fb",
        p: { xs: 2, md: 3 },
      }}
    >
      <Box sx={{ maxWidth: "1600px", mx: "auto" }}>
        <Typography
          variant="h2"
          sx={{
            color: "#13233f",
            fontSize: { xs: "2.25rem", md: "3.25rem" },
            fontWeight: 700,
            lineHeight: 1.1,
            mb: 1,
          }}
        >
          TM Knockout Search
        </Typography>

        <Typography
          sx={{
            color: "#61748d",
            fontSize: { xs: "1rem", md: "1.15rem" },
            mb: 3,
          }}
        >
          Search across trademarks with jurisdiction-specific filters and export the results.
        </Typography>

        <Paper
          component="form"
          onSubmit={handleSearch}
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: "18px",
            border: "1px solid #e3ebf4",
            backgroundColor: "#ffffff",
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ mb: 1, color: "#334155", fontSize: "1rem" }}>
                Mark Name
              </Typography>
              <TextField
                fullWidth
                name="markName"
                value={formValues.markName}
                onChange={handleFieldChange}
                placeholder="e.g. DOCGEN"
                sx={inputStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography sx={{ mb: 1, color: "#334155", fontSize: "1rem" }}>
                Jurisdiction
              </Typography>

              <TextField
                fullWidth
                select
                value={formValues.jurisdictions}
                onChange={handleJurisdictionChange}
                sx={inputStyles}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => {
                    const values = Array.isArray(selected)
                      ? selected
                      : typeof selected === "string"
                      ? selected.split(",")
                      : [];

                    return values
                      .map(
                        (val) =>
                          JURISDICTION_OPTIONS.find((opt) => opt.value === val)?.label
                      )
                      .filter(Boolean)
                      .join(", ");
                  },
                }}
              >
                {JURISDICTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox
                      checked={formValues.jurisdictions.includes(option.value)}
                    />
                    <Typography sx={{ ml: 1 }}>
                      {option.label}
                    </Typography>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography sx={{ mb: 1, color: "#334155", fontSize: "1rem" }}>
                Class
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {internationalClasses.map((classValue, index) => (
                  <Box
                    key={"international-class-" + index}
                    sx={{
                      display: "flex",
                      alignItems: "stretch",
                      gap: 1,
                    }}
                  >
                    <TextField
                      fullWidth
                      value={classValue}
                      onChange={(event) =>
                        setInternationalClasses((prev) =>
                          prev.map((currentValue, currentIndex) =>
                            currentIndex === index
                              ? sanitizeInternationalClassValue(event.target.value)
                              : currentValue
                          )
                        )
                      }
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      placeholder={index === 0 ? "Enter class (1-45)" : ""}
                      sx={inputStyles}
                    />
                    {index > 0 && (
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() =>
                          setInternationalClasses((prev) =>
                            prev.filter((_, currentIndex) => currentIndex !== index)
                          )
                        }
                        sx={{
                          minWidth: "56px",
                          px: 0,
                          color: "#475569",
                          borderColor: "#cbd5e1",
                          fontSize: "1.25rem",
                        }}
                      >
                        x
                      </Button>
                    )}
                  </Box>
                ))}
              </Box>

              <Button
                type="button"
                variant="outlined"
                onClick={() => setInternationalClasses((prev) => [...prev, ""])}
                sx={{
                  mt: 2,
                  color: "#334155",
                  borderColor: "#bfd0e3",
                  textTransform: "none",
                  fontSize: "1rem",
                  px: 2,
                  py: 1,
                }}
              >
                + Add Class
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  border: "1px solid #d9e3ef",
                  borderRadius: "16px",
                  backgroundColor: "#f8fbff",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    cursor: "pointer",
                  }}
                  onClick={() => setShowAdvancedFilters((prev) => !prev)}
                >
                  <Box>
                    <Typography sx={{ color: "#13233f", fontWeight: 700, fontSize: "1rem" }}>
                      Advanced Filters
                    </Typography>
                    <Typography sx={{ color: "#61748d", fontSize: "0.92rem" }}>
                      Refine your search with Owner, Date Range, and Mark Type filters
                    </Typography>
                  </Box>

                  <IconButton size="small" sx={{ color: "#334155" }}>
                    <KeyboardArrowDownRoundedIcon
                      sx={{
                        transform: showAdvancedFilters ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  </IconButton>
                </Box>

                <Collapse in={showAdvancedFilters}>
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Grid container spacing={3}>
                      {/* Owner */}
                      <Grid item xs={12} md={6}>
                        <Typography sx={{ mb: 1, color: "#334155", fontSize: "1rem" }}>
                          Owner
                        </Typography>
                        <TextField
                          fullWidth
                          name={isEuJurisdiction ? "applicantName" : "owner"}
                          value={isEuJurisdiction ? formValues.applicantName : formValues.owner}
                          onChange={handleFieldChange}
                          placeholder="e.g. Owner Name"
                          sx={inputStyles}
                        />
                      </Grid>

                      {/* Mark Type / Trademark Type */}
                      <Grid item xs={12} md={6}>
                        <Typography sx={{ mb: 1, color: "#334155", fontSize: "1rem" }}>
                          Mark Type
                        </Typography>
                        <TextField
                          fullWidth
                          select
                          name="trademarkType"
                          value={formValues.trademarkType}
                          onChange={handleFieldChange}
                          sx={inputStyles}
                        >
                          {AU_TRADEMARK_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      {/* Application Date Range */}
                      <Grid item xs={12} md={6}>
                        <Typography sx={{ mb: 1, color: "#334155", fontSize: "1rem" }}>
                          Application Date
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              type="date"
                              name="applicationDateFrom"
                              value={formValues.applicationDateFrom}
                              onChange={handleFieldChange}
                              label="From"
                              InputLabelProps={{ shrink: true }}
                              sx={inputStyles}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              type="date"
                              name="applicationDateTo"
                              value={formValues.applicationDateTo}
                              onChange={handleFieldChange}
                              label="To"
                              InputLabelProps={{ shrink: true }}
                              sx={inputStyles}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Registration Date Range */}
                      <Grid item xs={12} md={6}>
                        <Typography sx={{ mb: 1, color: "#334155", fontSize: "1rem" }}>
                          Registration Date
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              type="date"
                              name="registrationDateFrom"
                              value={formValues.registrationDateFrom}
                              onChange={handleFieldChange}
                              label="From"
                              InputLabelProps={{ shrink: true }}
                              sx={inputStyles}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              type="date"
                              name="registrationDateTo"
                              value={formValues.registrationDateTo}
                              onChange={handleFieldChange}
                              label="To"
                              InputLabelProps={{ shrink: true }}
                              sx={inputStyles}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* COMMENTED OUT - Mark Feature (EU only) */}
                      {/* {isEuJurisdiction && (
                        <Grid item xs={12} md={6}>
                          <Typography sx={{ mb: 1, color: "#334155", fontSize: "1rem" }}>
                            Mark Feature
                          </Typography>
                          <TextField
                            fullWidth
                            select
                            name="markFeature"
                            value={formValues.markFeature}
                            onChange={handleFieldChange}
                            sx={inputStyles}
                          >
                            {EU_MARK_FEATURE_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      )} */}

                      {/* COMMENTED OUT - Date Type Radio (EU only) */}
                      {/* {isEuJurisdiction && (
                        <Grid item xs={12} md={6}>
                          <Typography sx={{ mb: 1, color: "#334155", fontSize: "1rem" }}>
                            Date Type
                          </Typography>
                          <FormControl>
                            <RadioGroup
                              row
                              name="dateRangeType"
                              value={formValues.dateRangeType}
                              onChange={handleFieldChange}
                              sx={{ gap: 1.5, flexWrap: "wrap" }}
                            >
                              {DATE_RANGE_TYPE_OPTIONS.map((option) => (
                                <FormControlLabel
                                  key={option.value}
                                  value={option.value}
                                  control={
                                    <Radio
                                      size="small"
                                      sx={{
                                        p: 0.5,
                                        "&.Mui-checked": {
                                          color: "#1d70c9",
                                        },
                                      }}
                                    />
                                  }
                                  label={option.label}
                                  sx={{
                                    mr: 1,
                                    "& .MuiFormControlLabel-label": {
                                      fontSize: "0.92rem",
                                      color: "#334155",
                                    },
                                  }}
                                />
                              ))}
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                      )} */}

                      {/* COMMENTED OUT - Renewal Status (EU only) */}
                      {/* {isEuJurisdiction && (
                        <Grid item xs={12} md={6}>
                          <Typography sx={{ mb: 1, color: "#334155", fontSize: "1rem" }}>
                            Renewal Status
                          </Typography>
                          <TextField
                            fullWidth
                            select
                            name="renewalStatus"
                            value={formValues.renewalStatus}
                            onChange={handleFieldChange}
                            sx={inputStyles}
                          >
                            {EU_RENEWAL_STATUS_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      )} */}
                    </Grid>
                  </Box>
                </Collapse>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSearching}
                  startIcon={
                    isSearching ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : null
                  }
                  sx={{
                    backgroundColor: "#06066f",
                    textTransform: "none",
                    px: 3,
                    py: 1,
                    fontSize: "1rem",
                    "&.Mui-disabled": {
                      backgroundColor: "#7f8bb8",
                      color: "#ffffff",
                    },
                    "&:hover": {
                      backgroundColor: "#05055c",
                    },
                  }}
                >
                  {isSearching ? "Searching..." : "Search"}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleReset}
                  sx={{
                    color: "#475569",
                    borderColor: "#cbd5e1",
                    textTransform: "none",
                    px: 3,
                    py: 1,
                    fontSize: "1rem",
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {showResults && (
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: { xs: 2, md: 3 },
              borderRadius: "18px",
              border: "1px solid #e3ebf4",
              backgroundColor: "#ffffff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                gap: 2,
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <Typography
                variant="h4"
                sx={{ color: "#13233f", fontWeight: 700, fontSize: "2rem" }}
              >
                Results
              </Typography>

              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <TextField
                  select
                  size="small"
                  value={sortBy}
                  onChange={handleSortByChange}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="applicationDate">Application Date</MenuItem>
                  <MenuItem value="registrationDate">Registration Date</MenuItem>
                </TextField>
                <Button
                  variant="outlined"
                  onClick={handleSortOrderToggle}
                  sx={{
                    borderColor: "#cbd5e1",
                    color: "#334155",
                    textTransform: "none",
                    px: 2,
                  }}
                >
                  {sortOrder === "asc" ? "↑ Ascending" : "↓ Descending"}
                </Button>
                <TextField
                  select
                  size="small"
                  value={exportType}
                  onChange={(event) => setExportType(event.target.value)}
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="excel">Excel</MenuItem>
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="pdf">PDF</MenuItem>
                </TextField>
                <Button
                  variant="contained"
                  onClick={handleExport}
                  sx={{
                    backgroundColor: "#06066f",
                    textTransform: "none",
                    px: 3,
                    "&:hover": {
                      backgroundColor: "#05055c",
                    },
                  }}
                >
                  Export
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Checkbox checked={allSelected} onChange={handleSelectAll} />
              <Typography sx={{ color: "#475569", fontSize: "1.05rem" }}>
                Select All
              </Typography>
            </Box>

            <Typography sx={{ color: "#7a8ca5", fontSize: "1.05rem", mb: 3 }}>
              {getDisplayedResultsInfo()}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {getSortedAndPaginatedResults.map((result) => (
                <TrademarkResultCard
                  key={result.id}
                  result={result}
                  isChecked={selectedResultIds.includes(result.id)}
                  onToggle={() => handleToggleResult(result.id)}
                  onViewDetail={() => handleViewDetail(result)}
                />
              ))}
            </Box>

            {totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2,
                  mt: 4,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  sx={{
                    borderColor: "#cbd5e1",
                    color: "#334155",
                    textTransform: "none",
                  }}
                >
                  Previous
                </Button>
                <Typography sx={{ color: "#475569", fontSize: "1rem" }}>
                  Page {currentPage} of {totalPages}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  sx={{
                    borderColor: "#cbd5e1",
                    color: "#334155",
                    textTransform: "none",
                  }}
                >
                  Next
                </Button>
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default TrademarkSearch;
